import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

const VIEWPORT_PAD = 8;
/** Target width for TabSense-style copy (~3–4 lines); capped by viewport below. */
const TOOLTIP_MAX_WIDTH = 320;

/**
 * Disabled controls and layout/stacking break MDBTooltip popper placement.
 * Renders markup/CSS compatible with Catalog MDBTooltip: `.tooltip`,
 * `.tooltip-arrow`, `.tooltip-inner`, `bs-tooltip-top` / `bs-tooltip-bottom`.
 *
 * Position: centered on the control, then **clamped** so the full tooltip stays inside the viewport.
 */
export default function DisabledControlTooltipWrap({
  active,
  title,
  children,
  className = "",
  style: styleProp,
}) {
  const tipText = title != null && title !== "" ? String(title) : "";
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({
    left: 0,
    top: 0,
    transform: "translate(-50%, -100%)",
    placement: "top",
  });
  const wrapRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimerRef = useRef(null);

  const reposition = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const anchor = wrap.firstElementChild;
    const r =
      anchor && typeof anchor.getBoundingClientRect === "function"
        ? anchor.getBoundingClientRect()
        : wrap.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth;
    const tipW = Math.min(TOOLTIP_MAX_WIDTH, vw - 2 * VIEWPORT_PAD);
    const halfW = tipW / 2;
    const centerX = r.left + r.width / 2;
    const left = Math.max(
      VIEWPORT_PAD + halfW,
      Math.min(vw - VIEWPORT_PAD - halfW, centerX)
    );

    const aboveTop = r.top - margin;
    const placeBelow = aboveTop < 48;

    const top = placeBelow ? r.bottom + margin : r.top - margin;
    const transform = placeBelow
      ? "translate(-50%, 0)"
      : "translate(-50%, -100%)";
    const placement = placeBelow ? "bottom" : "top";

    setCoords({
      left,
      top,
      transform,
      placement,
    });
  }, []);

  const applyViewportClamp = useCallback(() => {
    const el = tooltipRef.current;
    if (!el || typeof el.getBoundingClientRect !== "function") return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let sx = 0;
    if (rect.left < VIEWPORT_PAD) sx = VIEWPORT_PAD - rect.left;
    let shiftedRight = rect.right + sx;
    if (shiftedRight > vw - VIEWPORT_PAD) {
      sx += vw - VIEWPORT_PAD - shiftedRight;
    }

    let sy = 0;
    if (rect.top < VIEWPORT_PAD) sy = VIEWPORT_PAD - rect.top;
    let shiftedBottom = rect.bottom + sy;
    if (shiftedBottom > vh - VIEWPORT_PAD) {
      sy += vh - VIEWPORT_PAD - shiftedBottom;
    }

    if (sx === 0 && sy === 0) return;
    setCoords((c) => ({
      ...c,
      left: c.left + sx,
      top: c.top + sy,
    }));
  }, []);

  const clearHideTimer = () => {
    if (hideTimerRef.current != null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const scheduleHide = () => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setVisible(false);
      hideTimerRef.current = null;
    }, 120);
  };

  useEffect(() => () => clearHideTimer(), []);

  /** After layout, nudge tooltip so it is fully visible (horizontal + vertical). */
  useLayoutEffect(() => {
    if (!visible || !tipText) return;
    applyViewportClamp();
  }, [visible, tipText, coords.left, coords.top, coords.transform, applyViewportClamp]);

  useEffect(() => {
    if (!visible || !tipText) return;
    const onScroll = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", reposition);
    };
  }, [visible, tipText, reposition]);

  if (!active) return children;

  const show = () => {
    if (!tipText) return;
    clearHideTimer();
    reposition();
    setVisible(true);
  };

  const innerWidth = `min(${TOOLTIP_MAX_WIDTH}px, calc(100vw - ${VIEWPORT_PAD * 2}px))`;

  return (
    <span
      ref={wrapRef}
      className={`d-inline-flex align-items-center ${className}`.trim()}
      style={{
        position: "relative",
        cursor: "not-allowed",
        verticalAlign: "middle",
        maxWidth: "100%",
        ...styleProp,
      }}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
    >
      {children}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          cursor: "not-allowed",
        }}
      />
      {visible &&
        tipText &&
        typeof document !== "undefined" &&
        document.body &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className={`tooltip fade show bs-tooltip-${coords.placement}`}
            style={{
              position: "fixed",
              left: coords.left,
              top: coords.top,
              transform: coords.transform,
              zIndex: 2147483646,
              opacity: 1,
              pointerEvents: "none",
              margin: 0,
              maxWidth: "none",
            }}
          >
            <div className="tooltip-arrow" />
            <div
              className="tooltip-inner"
              style={{
                width: innerWidth,
                maxWidth: "100%",
                boxSizing: "border-box",
                textAlign: "center",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {tipText}
            </div>
          </div>,
          document.body
        )}
    </span>
  );
}
